import { IRequestShortcutConfig } from "angular";

import { ICollection } from "@gtmhub/models";
import { EnvironmentService } from "@gtmhub/env";
import { AssigneeType, IAssignee } from "@gtmhub/assignees";
import { AutomationTriggerType, IAutomation } from "@gtmhub/automation/models/automation";
import { IGoal } from "@gtmhub/goals/models";
import { IInsightboard } from "@gtmhub/insightboards";
import { IInsight } from "@gtmhub/insights";
import { IKpi } from "@gtmhub/kpis";
import { IList } from "@gtmhub/lists";
import { IMetric } from "@gtmhub/okrs/metrics";
import { IRole } from "@gtmhub/roles";
import { IPlanningSession, PlanningSessionStatus } from "@gtmhub/sessions";
import { ITask } from "@gtmhub/tasks";
import { ITeam } from "@gtmhub/teams";
import { IUser } from "@gtmhub/users";
import { IWhiteboard } from "@gtmhub/whiteboards";
import { buildSearchConditions, searchCollectionsMap, searchFieldsMap } from "@gtmhub/search/utils/global-search";

export type SearchCollectionType =
  | "sessions"
  | "goals"
  | "metrics"
  | "tasks"
  | "users"
  | "teams"
  | "lists"
  | "kpis"
  | "insights"
  | "dashboards"
  | "automations"
  | "whiteboards"
  | "roles";

export interface IGoalParentSearchApi {
  goalId: string;
  metricGoalId: string;
  metricId: string;
  sessionId: string;
}

type SearchItemInterface = {
  sessions: Partial<IPlanningSession>;
  goals: Partial<IGoal> & {
    parent?: IGoalParentSearchApi;
    session?: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _id: string;
      status: PlanningSessionStatus;
      title: string;
    };
  };
  metrics: Partial<IMetric> & {
    session?: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _id: string;
      status: PlanningSessionStatus;
      title: string;
    };
  };
  tasks: Partial<ITask>;
  users: Partial<IUser> & {
    fullName: string;
    auth0Cache: {
      appmetadata: { accountId: string };
      email: string;
      emailverified: false;
      familyname: string;
      givenname: string;
      groups: [];
      // eslint-disable-next-line @typescript-eslint/naming-convention
      identities: [{ connection: string; provider: string; user_id: string }];
      name: string;
      picture: string;
      userid: string;
      usermetadata: { firstName: string; lastName: string };
      username: string;
    };
    teams: {
      id: string;
      name: string;
    }[];
  };
  teams: Partial<ITeam>;
  lists: Partial<IList> & { listType: number };
  kpis: Partial<IKpi> & { assignee?: IAssignee };
  insights: Partial<IInsight>;
  dashboards: Partial<IInsightboard> & { description?: string };
  automations: Partial<IAutomation<AutomationTriggerType>>;
  whiteboards: Partial<IWhiteboard>;
  roles: Partial<IRole>;
};

type SearchItemCollectionTypeToInterface = {
  [CollectionType in SearchCollectionType]: SearchItemInterface[CollectionType];
};

export type SearchItem<T extends keyof SearchItemCollectionTypeToInterface> = {
  id: string;
  collectionName: T;
  fields: SearchItemCollectionTypeToInterface[T];
  score: number;
};

export interface ISearchQueryParams {
  skip?: number;
  take?: number;
}

export enum SearchOperatorsEnum {
  matchPrefix = 0,
  matchAny = 4,
}

export interface ISearchBody {
  searchTerm?: string;
  operator?: SearchOperatorsEnum;
  searchRequests: ISearchRequestsBody[];
}

export interface ISearchRequestsBody {
  collectionName: string;
  // empty array if operator is 4
  searchFields?: ISearchField[];
  responseFields?: string[];
  searchConditions?: ISearchCondition[];
}

export interface ISearchCondition {
  fieldName: string;
  fieldCondition: ISearchFieldCondition;
}

export interface ISearchSettings {
  activeUsersOnly?: boolean;
}

export interface ISearchFieldCondition {
  operator: "is" | "isNot" | "in";
  value: string | string[];
}

export interface ISearchField {
  name: string;
}

export const compoundSuffix = "Compound";

export class SearchService {
  public static $inject = ["$http", "EnvironmentService"];

  constructor(private $http: ng.IHttpService, private env: EnvironmentService) {}

  public getSomeSearchData<T>(searchBody: ISearchBody, queryParams?: ISearchQueryParams): ng.IPromise<ICollection<T>> {
    const url = this.env.getSearchEndpoint(`/multi`);
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.post<ICollection<T>>(url, searchBody, query).then((response) => response.data);
  }

  public getSearchBody(searchTerm: string, currentFacet: string, settings: ISearchSettings = {}): ISearchBody {
    const compound = searchCollectionsMap[currentFacet].length > 1;

    return {
      searchTerm,
      operator: 0,
      searchRequests: searchCollectionsMap[currentFacet].map((collection) => {
        const collectionKey = compound ? collection + compoundSuffix : collection;

        return {
          collectionName: collection,
          searchFields: searchFieldsMap[collectionKey].map((field: string) => {
            return {
              name: field,
            };
          }),
          searchConditions: buildSearchConditions(collection, settings),
        };
      }),
    };
  }

  public getAssigneeSearchCollections(assigneeType: AssigneeType, searchTerm: string, includeActiveUsersOnly = true): ISearchRequestsBody[] {
    const searchCollections: ISearchRequestsBody[] = [
      {
        collectionName: "teams",
        searchFields: searchTerm ? [{ name: "name" }, { name: "description" }] : [],
        searchConditions: [],
      },
      {
        collectionName: "users",
        searchFields: searchTerm ? [{ name: "fullName" }, { name: "auth0Cache.email" }] : [],
        searchConditions: includeActiveUsersOnly
          ? [
              {
                fieldName: "isActive",
                fieldCondition: {
                  operator: "isNot",
                  value: "false",
                },
              },
            ]
          : [],
      },
    ];

    if (!assigneeType || assigneeType === "both") {
      return searchCollections;
    }

    if (assigneeType === "user") {
      return [searchCollections.find((collection) => collection.collectionName === "users")];
    }

    if (assigneeType === "team") {
      return [searchCollections.find((collection) => collection.collectionName === "teams")];
    }
  }
}
